// ---------------------------
// src/HomeModern.jsx (v2 clean)
// ---------------------------

import React, { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { tUI } from "./i18n";
import CreditsBadge from "./CreditsBadge";
import LanguageSelector from "./LanguageSelector";
import { subscribe } from "./paymentsClient";
import api from "./apiClient";
import CVHtmlPreview from "./CVHtmlPreview";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
const AVATAR_1 = "/photo/avatar-1.png"; // Ansthwon Ten
const AVATAR_2 = "/photo/avatar-2.png"; // Sarah M.
const AVATAR_3 = "/photo/avatar-3.png"; // Daniel K.



/* ===== Auto-fit title (kept) ===== */



/* ===== Inline SVG Icons to match reference ===== */
const Lock = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const Shield = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const Target = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);
const Spark = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <path d="M12 2v6m0 8v6M2 12h6m8 0h6M5 5l4 4m6 6 4 4m0-14-4 4M9 15l-4 4" />
  </svg>
);
const Pencil = (p) => (
  <svg
    viewBox="0 0 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <path d="M11 4h2l7 7-2 2-7-7V4z" />
    <path d="M4 13l7 7 9-9" />
  </svg>
);
const Device = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={p.className}
  >
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M7 21h10" />
  </svg>
);

/* Logos (monochrome, compact) */
const GoogleLogo = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-label="Google"
  >
    <path d="M12 2a10 10 0 1 0 10 10v-1h-9v3h5.8A7.5 7.5 0 1 1 12 4.5c1.9 0 3.6.7 4.9 1.8l2-2.1A9.8 9.8 0 0 0 12 2z" />
  </svg>
);
const MicrosoftLogo = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    aria-label="Microsoft"
  >
    <g fill="#6B7280">
      <rect x="3" y="3" width="8" height="8" />
      <rect x="13" y="3" width="8" height="8" />
      <rect x="3" y="13" width="8" height="8" />
      <rect x="13" y="13" width="8" height="8" />
    </g>
  </svg>
);
const AppleLogo = ({ className }) => (
  <svg
    viewBox="0 0 256 315"
    className={className}
    aria-label="Apple"
    fill="#A3AAAE"
  >
    <path d="M213.8 167.1c-.4-41.5 33.9-61.5 35.5-62.6-19.3-28.1-49.3-32-59.9-32.5-25.5-2.6-49.7 15-62.6 15-13.1 0-32.9-14.6-54.1-14.2-27.9.4-53.6 16.2-67.9 41.1C-23.3 153.3-5.1 231 26 276.6c14.2 21 31 44.6 53.4 43.8 21.6-.8 29.8-14.2 56-14.2 26.2 0 33.6 14.2 56.4 13.7 23.3-.4 38.1-21.3 52.2-42.6 16.8-24.6 23.8-48.5 24.2-49.7-.5-.2-46.3-17.8-46.4-60.5zM173.7 49.4c11.8-14.3 19.8-34.3 17.6-54.4-17 0-37.8 11.4-50.1 25.6-11 12.7-20.7 33-18.1 52.3 19.1 1.5 38.7-9.7 50.6-23.5z" />
  </svg>
);

function Badge({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[#06105933] px-3 py-1.5">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100">
        <Icon className="h-[14px] w-[14px]" />
      </span>
      <span className="text-[13px] font-medium">{label}</span>
    </div>
  );
}

function BrandRow() {
  const items = [
    { name: "Google", Icon: GoogleLogo },
    { name: "Microsoft", Icon: MicrosoftLogo },
    { name: "Apple", Icon: AppleLogo },
  ];
  return (
    <div className="mx-auto mt-6 flex max-w-[700px] items-center justify-center gap-10 md:gap-14 text-slate-500">
      {items.map(({ name, Icon }) => (
        <div key={name} className="flex items-center gap-2">
          <span className="grid h-6 w-6 place-items-center">
            <Icon
              className={`align-middle ${
                name === "Apple" ? "h-[18px] w-[18px]" : "h-[14px] w-[14px]"
              }`}
            />
          </span>
          <span className="text-[15px] leading-none">{name}</span>
        </div>
      ))}
    </div>
  );
}

export default function HomeModern({
  language,
  file,
  setFile,
  onConvert,
  service,
  onSelectService,
  canUse,
  loading,
  error,
  user,
  onSignIn,
  onSignOut,
  setLanguage,
  onOpenSection,
  hasActivePlan,
  hasEverPaid,
  currentPlan,
  hideMainContent = false,
  previewPdfUrl,
  parsedData,
  onLandingMatchPreview,
  onLandingUnlock,
  onClearPreview,
  blogUrl,

}) {
  

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [heroTab, setHeroTab] = useState("scan");
  const [landingJd, setLandingJd] = useState("");
  const trackEvent = (eventName, params = {}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      page_path: window.location.pathname,
      language,
      ...params,
    });
  }
};

  const [numPages, setNumPages] = useState(0);
  const previewPageWidth = Math.min(window.innerWidth - 80, 760);

  const isPaidUser = !!hasActivePlan;
  const shouldShowFreeScan = !isPaidUser && !hasEverPaid;

  const onPreviewLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // NEW: which tools are unlocked by the plan
  const plan = (currentPlan || "none").toLowerCase();

  const canUseAts =
    hasActivePlan && ["standard", "advanced", "premium"].includes(plan);
  const canUseAi =
    hasActivePlan && ["advanced", "premium"].includes(plan);
  const canUseBuilder =
    hasActivePlan && plan === "premium";

  // 🔹 Helper to map "low/medium/high" to translated label
  const getLevelLabel = (rawLevel) => {
    const l = (rawLevel || "Low").toLowerCase();
    if (l === "high")   return tUI(language, "ats_level_high");
    if (l === "medium") return tUI(language, "ats_level_medium");
    return tUI(language, "ats_level_low");
  };

  const getLevelDescription = (rawLevel) => {
    const lvl = (rawLevel || "Low").toLowerCase();
    if (lvl === "high") return tUI(language, "ats_desc_high");
    if (lvl === "medium") return tUI(language, "ats_desc_medium");
    return tUI(language, "ats_desc_low");
  };

    // Visual theme for ATS result card by level
  const getLevelStyles = (rawLevel) => {
    const lvl = (rawLevel || "low").toLowerCase();

    // ✅ High = green with checkmark
    if (lvl === "high") {
      return {
        card: "border-[#BBF7D0] bg-[#F0FDF4]",   // light green
        iconBorder: "border-[#BBF7D0]",
        accent: "text-[#15803D]",                // dark green text
        iconSymbol: "✓",
      };
    }

    // 🟠 Medium = keep current orange theme
    if (lvl === "medium") {
      return {
        card: "border-[#FDBA74] bg-[#FFF7ED]",   // orange
        iconBorder: "border-[#FDBA74]",
        accent: "text-[#EA580C]",
        iconSymbol: "⚠️",
      };
    }

    // 🔴 Low = light red warning
    return {
      card: "border-[#FCA5A5] bg-[#FEF2F2]",     // light red
      iconBorder: "border-[#FCA5A5]",
      accent: "text-[#B91C1C]",                  // dark red text
      iconSymbol: "⚠️",
    };
  };

    // ✅ FREE PREVIEW LIMIT (like FREE_SCAN_LIMIT)
  const FREE_PREVIEW_LIMIT = 2;

  // how many free previews already used on this device
  const [freePreviewCount, setFreePreviewCount] = useState(0);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    const raw = window.localStorage.getItem("yscv_free_preview_count");
    const prev = raw ? parseInt(raw, 10) || 0 : 0;
    setFreePreviewCount(prev);
  }, []);

  const freePreviewUsed = freePreviewCount >= FREE_PREVIEW_LIMIT;
 

  // 🔢 how many free scans allowed (change this for tests)
  const FREE_SCAN_LIMIT = 5; // e.g. 5 scans while you test. Later set to 1.

 

  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  //const [previewError, setPreviewError] = useState("");
  const [scanResult, setScanResult] = useState(null);

  // how many free scans already used on this device
  const [freeScanCount, setFreeScanCount] = useState(0);

  useEffect(() => {
    const raw = window.localStorage.getItem("yscv_free_scan_count");
    const prev = raw ? parseInt(raw, 10) || 0 : 0;
    setFreeScanCount(prev);
  }, []);

  const freeScanUsed = freeScanCount >= FREE_SCAN_LIMIT;


  // Start Stripe checkout from landing pricing cards
  const handleLandingSubscribe = async (planKey) => {
    if (!user) {
      onSignIn?.();
      return;
    }
    try {
      await subscribe(planKey);
    } catch (err) {
      console.error(err);
      alert("Could not start checkout. Please try again.");
    }
  };

  // Pricing plans with features from i18n
  const plans = [
    {
      id: "ats",
      planKey: "standard",
      title: tUI(language, "card_ats_title"),
      subtitle: tUI(language, "card_ats_desc"),
      price: "€10.99",
      features: [
        tUI(language, "plan_ats_feature1"),
        tUI(language, "plan_ats_feature2"),
        tUI(language, "plan_ats_feature3"),
        tUI(language, "plan_ats_feature4"),
      
      ],
    },
    {
      id: "ai_matching",
      planKey: "advanced",
      title: tUI(language, "card_ai_title"),
      subtitle: tUI(language, "card_ai_desc"),
      price: "€19.99",
      features: [
        tUI(language, "plan_ai_feature1"),
        tUI(language, "plan_ai_feature2"),
        tUI(language, "plan_ai_feature3"),
        tUI(language, "plan_ai_feature4"),
        tUI(language, "plan_ai_feature5"),
        tUI(language, "plan_ai_feature6"),
      ],
    },
    {
      id: "builder",
      planKey: "premium",
      title: tUI(language, "card_builder_title"),
      subtitle: tUI(language, "card_builder_desc"),
      price: "€8.99",
      features: [
        tUI(language, "plan_builder_feature1"),
        tUI(language, "plan_builder_feature2"),
        tUI(language, "plan_builder_feature3"),
        tUI(language, "plan_builder_feature4"),
        tUI(language, "plan_builder_feature5"),
        tUI(language, "plan_builder_feature6"),
        tUI(language, "plan_builder_feature7"),
      ],
    },
  ];
   const handleFreeScan = async () => {
    // already reached the limit?
    if (freeScanUsed) {
      setScanError(
        tUI(language, "free_scan_used") ||
          "Your free scan has already been used. Please upgrade to continue."
      );
      return;
    }

    if (!file) {
      setScanError(
        tUI(language, "msg_no_file") || "Please upload a PDF first."
      );
      return;
    }

    setScanError("");
    onClearPreview?.();
    setScanLoading(true);
    trackEvent("free_ats_scan_started", {
      tool: "free_scan",
    });

    try {
      
      const form = new FormData();
      form.append("file", file);

      const res = await api.post("/free_ats_scan", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setScanResult(res.data);
      trackEvent("free_ats_scan_completed", {
        tool: "free_scan",
        score: res.data?.score,
        level: res.data?.level,
      });

      // increment counter
      const next = freeScanCount + 1;
      setFreeScanCount(next);
      window.localStorage.setItem("yscv_free_scan_count", String(next));
    } catch (e) {
      console.error(e);
      setScanResult(null);
      setScanError(
        e.userMessage ||
          "We couldn’t scan your CV. Please try again in a moment."
      );
    } finally {
      setScanLoading(false);
    }
  };

  const handleFreePreview = async () => {
      setScanResult(null); // ✅ ensure scan result box is hidden
      if (freePreviewUsed) {
        setPreviewError("You used all free previews. Please unlock to continue.");
        return;
      }

      if (!file) {
        setPreviewError("Please upload a PDF first.");
        return;
      }

      if (!landingJd.trim()) {
        setPreviewError("Please paste the job description.");
        return;
      }

      setPreviewError("");
      trackEvent("free_cv_preview_started", {
        tool: "free_preview",
        has_job_description: true,
      });

      // run your existing preview pipeline
      await onLandingMatchPreview?.(file, landingJd);
      trackEvent("free_cv_preview_completed", {
        tool: "free_preview",
      });

      // ✅ increment counter
      const next = freePreviewCount + 1;
      setFreePreviewCount(next);
      window.localStorage.setItem("yscv_free_preview_count", String(next));
    };
          

    return (
    <div id="yscv" className="flex flex-col bg-white text-slate-900">
      {/* Navbar */}
      <header className="bg-white fixed top-0 left-0 right-0 z-20">
        <nav className="mx-auto flex max-w-[1140px] items-center justify-between px-4 py-4">
          {/* Logo */}
          <button
            className="text-[20px] font-extrabold tracking-tight"
            onClick={() => {
              setMobileMenuOpen(false);
              onOpenSection?.(null);

              // 🔹 Reset URL to root so next reload goes to landing page
              if (window.location.pathname !== "/") {
                window.history.pushState({}, "", "/");
              }
            }}
            aria-label="Home"
          >
            YourSmartCV
          </button>

          {/* Desktop nav */}
          <div className="yscv-desktop-nav">
            <button
              onClick={() => onOpenSection?.("about")}
              className="yscv-nav-link hover:text-slate-600"

            >
              {tUI(language, "nav_about")}
            </button>

            <a
              href={blogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="yscv-nav-link hover:text-slate-600"
            >
              {tUI(language, "nav_blog")}
            </a>

            <button
              onClick={() => onOpenSection?.("pricing")}
              className="yscv-nav-link hover:text-slate-600"
            >
              {tUI(language, "nav_pricing")}
            </button>

            {!user ? (
              <button
                onClick={() => {
                  trackEvent("sign_in_clicked", {
                    location: "mobile_nav",
                  });
                  setMobileMenuOpen(false);
                  onSignIn?.();
                }}
                className="block w-full text-left text-[14px] py-1.5"
              >
                {tUI(language, "nav_signin")}
              </button>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onSignOut?.();
                }}
                className="block w-full text-left text-[14px] py-1.5"
              >
                {tUI(language, "nav_signout")}
              </button>
            )}

            <LanguageSelector
              value={language}
              onChange={(lng) => setLanguage?.(lng)}
              showLabel={false}
            />
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-md border border-slate-200 p-2 text-slate-700"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              // X icon
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 6l12 12" />
                <path d="M18 6l-12 12" />
              </svg>
            ) : (
              // Burger icon
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile nav menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="mx-auto max-w-[1140px] px-4 py-3 space-y-2">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenSection?.("about");
                }}
                className="block w-full text-left text-[14px] py-1.5"
              >
                {tUI(language, "nav_about")}
              </button>

              <a
                href={blogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left text-[14px] py-1.5"
                onClick={() => setMobileMenuOpen(false)}
              >
                {tUI(language, "nav_blog")}
              </a>

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenSection?.("pricing");
                }}
                className="block w-full text-left text-[14px] py-1.5"
              >
                {tUI(language, "nav_pricing")}
              </button>

              {!user ? (
                <button
                  onClick={() => {
                    trackEvent("sign_in_clicked", {
                      location: "mobile_nav",
                    });
                    setMobileMenuOpen(false);
                    onSignIn?.();
                  }}
                  className="block w-full text-left text-[14px] py-1.5"
                >
                  {tUI(language, "nav_signin")}
                </button>
              ) : (
                <button
                  onClick={() => {
                    trackEvent("sign_out_clicked", {
                      location: "mobile_nav",
                    });
                    setMobileMenuOpen(false);
                    onSignOut?.();
                  }}
                  className="block w-full text-left text-[14px] py-1.5"
                >
                  {tUI(language, "nav_signout")}
                </button>
              )}

              <div className="pt-2">
                <LanguageSelector
                  value={language}
                  onChange={(lng) => setLanguage?.(lng)}
                  showLabel={true}
                />
              </div>
            </div>
          </div>
        )}
      </header>


      {!hideMainContent && (
      <>
        {/* Hero */}
        <section className="mx-auto max-w-[1140px] px-4 pt-24 pb-4 text-center">
          <h1 className="hero-title text-slate-900 leading-tight">
            {hasActivePlan
              ? tUI(language, "services_title")
              : tUI(language, "hero_title")}
          </h1>

          {/* Subtitle – changes based on user status */}
          {!isPaidUser ? (
            <p className="hero-sub mt-3">
              {tUI(language, "hero_sub")}
            </p>
          ) : (
            <p className="hero-sub mt-3">
              {tUI(language, "services_subtitle")}
            </p>
          )}


          
         {/* Upload card with 2 tabs (Scan / Match JD) */}
        {shouldShowFreeScan && (
          <div className="mx-auto mt-8 w-full max-w-[550px] rounded-xl border border-[#06105933] p-5 shadow-sm">
            {/* Tabs */}
            <div className="mb-4 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setHeroTab("scan");
                  setPreviewError("");
                  onClearPreview?.();        // optional
                  // If you want: hide the preview box when going back to Scan
                  // (requires a prop from App, so skip if you want simple)
                }}
                className={
                  "flex-1 rounded-lg px-3 py-2 text-[14px] font-semibold border " +
                  (heroTab === "scan"
                    ? "bg-[#061059] text-white border-[#061059]"
                    : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50")
                }
              >
                {tUI(language, "tab_scan")}
              </button>

              <button
                type="button"
                onClick={() => {
                  setHeroTab("match");
                  setScanResult(null);        // ✅ hides the free scan result box
                  setScanError("");           // optional: clear scan error
                }}
                className={
                  "flex-1 rounded-lg px-3 py-2 text-[14px] font-semibold border " +
                  (heroTab === "match"
                    ? "bg-[#061059] text-white border-[#061059]"
                    : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50")
                }
              >
                {tUI(language, "tab_match")}
              </button>
            </div>

            {/* Upload label row */}
            <div className="flex items-center justify-between mb-3 gap-3">
              <label className="block text-left text-[14px] font-medium text-slate-700">
                {tUI(language, "hero_upload_label")}
              </label>
              <CreditsBadge language={language} />
            </div>

            {/* File chooser row */}
            <div className="flex items-stretch overflow-hidden rounded-lg border border-[#06105933]">
              <label className="cursor-pointer whitespace-nowrap px-4 py-2 text-[14px] font-medium text-slate-900 bg-white hover:bg-slate-50">
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                Choose File
              </label>
              <span className="flex-1 px-4 py-2 text-left text-[14px] text-slate-600 bg-white border-l border-[#06105933]">
                {file?.name || "No file chosen"}
              </span>
            </div>

            {/* JD textarea only for tab 2 */}
            {heroTab === "match" && (
              <div className="mt-4 text-left">
                <label className="block text-[14px] font-medium text-slate-700 mb-1">
                  {tUI(language, "label_job_desc")}
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  rows={4}
                  value={landingJd}
                  onChange={(e) => setLandingJd(e.target.value)}
                />
              </div>
            )}

            {/* Single primary CTA */}
            <button
              className="mt-4 w-full rounded-lg bg-[#061059] px-4 py-2.5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#0A1575] focus:outline-none focus:ring-2 focus:ring-[#0A1575] disabled:opacity-60"
              onClick={() => {
                if (heroTab === "scan") return handleFreeScan();
                 return handleFreePreview();
              }}
             disabled={
              loading ||
              scanLoading ||
              !file ||
              (heroTab === "scan" && freeScanUsed) ||
              (heroTab === "match" && (!landingJd.trim() || freePreviewUsed))
            }
            >
              {loading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Working…
                </span>
              ) : heroTab === "scan" ? (
                tUI(language, "cta_scan")
              ) : (
                tUI(language, "cta_match")
              )}
            </button>

            {/* Errors */}
            {scanError && heroTab === "scan" && (
              <p className="error mt-2 text-red-600 text-sm">{scanError}</p>
            )}
            {error && heroTab === "match" && (
              <p className="error mt-2 text-red-600 text-sm">{error}</p>
            )}

            {previewError && heroTab === "match" && (
                <p className="error mt-2 text-red-600 text-sm">{previewError}</p>
              )}

            {/* Micro trust + price hint */}
            <p className="mt-3 text-center text-[12px] text-slate-500">
              {tUI(language, "hint_secure")}
            </p>
            {/* <p className="mt-1 text-center text-[12px] text-slate-500">
              Full access: €24.99 for 30 days · No auto-renew
            </p> */}
          </div>
        )}
        {/* ✅ Preview (shows after Convert+Match JD returns preview JSON) */}
        {shouldShowFreeScan && (previewPdfUrl || loading) && (
          <div className="mx-auto mt-6 w-full max-w-[900px] px-4">
            <div className="rounded-xl border border-[#06105933] bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-left text-[15px] font-semibold text-slate-900">
                  {tUI(language, "preview_title")}
                </h3>

                <button
                  type="button"
                  className="rounded-lg bg-[#061059] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#0A1575]"
                  onClick={() => {
                    trackEvent("unlock_full_cv_clicked", {
                      location: "preview_or_video",
                      plan: "premium",
                    });

                    onLandingUnlock?.();
                  }}
                >
                  {tUI(language, "preview_unlock")}
                </button>
              </div>

              <p className="mb-3 text-left text-[12px] text-slate-500">
                {tUI(language, "preview_sub")}
              </p>
              <div className="h-[520px] overflow-auto rounded-lg border border-slate-200 bg-white">
               {loading ? (
                  <div className="h-full w-full grid place-items-center text-slate-500 text-sm">
                    <div className="flex flex-col items-center gap-3">
                      <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#061059] border-t-transparent" />
                      <span>{tUI(language, "preview_generating")}</span>
                    </div>
                  </div>
                ) : previewPdfUrl ? (
                  <div
                    className="flex justify-center bg-slate-50 py-4"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ userSelect: "none" }}
                  >
                    <Document
                      file={previewPdfUrl}
                      onLoadSuccess={onPreviewLoadSuccess}
                      loading={
                        <div className="py-8 text-sm text-slate-500">
                          {tUI(language, "preview_loading")}
                        </div>
                      }
                      error={
                        <div className="py-8 text-sm text-red-600">
                          {tUI(language, "preview_error")}
                        </div>
                      }
                    >
                      {Array.from(new Array(numPages), (_, index) => (
                        <div key={`page_${index + 1}`} className="mb-4">
                          <Page
                            pageNumber={index + 1}
                            width={previewPageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </div>
                      ))}
                    </Document>
                  </div>
                ) : (
                  <div className="h-full w-full grid place-items-center text-slate-500 text-sm">
                    {tUI(language, "preview_no")}
                  </div>
                )}
              </div>
              <p className="mt-3 text-left text-[12px] text-slate-500">
                {tUI(language, "preview_bottom")}
              </p>
            </div>
          </div>
        )}

          {/* 🔶 ATS score result – put it HERE, after upload card, before logos */}
                    {/* ATS score result – appears only after a free scan */}
          {shouldShowFreeScan && scanResult && (() => {
            const levelStyles = getLevelStyles(scanResult.level);

            return (
              <div
                className={
                  "mx-auto mt-6 w-full max-w-[550px] rounded-xl border px-5 py-4 text-left shadow-sm " +
                  levelStyles.card
                }
              >
                <div className="flex items-start gap-3">
                  {/* Icon circle */}
                  <div
                    className={
                      "mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-white border " +
                      levelStyles.iconBorder
                    }
                  >
                    <span className="text-[18px]">{levelStyles.iconSymbol}</span>
                  </div>

                  <div className="flex-1">
                    {/* Title: Your CV score: XX% (Low/Medium/High) */}
                    <p className="text-[15px] font-semibold text-slate-900">
                      {tUI(language, "ats_result_title")
                        .replace("{score}", scanResult.score ?? 0)
                        .replace("{level}", getLevelLabel(scanResult.level))}
                    </p>

                    {/* Fixed explanation per level */}
                    <p className="mt-2 text-[13px] leading-5 text-slate-700">
                      {getLevelDescription(scanResult.level)}
                    </p>

                    {/* Colored info line */}
                    <p
                      className={
                        "mt-2 text-[13px] font-semibold " + levelStyles.accent
                      }
                    >
                      {tUI(language, "ats_result_boost_cta")}
                    </p>

                    {/* Button → scrolls to pricing */}
                    <button
                      type="button"
                      className="mt-4 w-full rounded-lg bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-900 border border-slate-200 hover:bg-slate-50"
                      onClick={() => onOpenSection?.("pricing")}
                    >
                      {tUI(language, "ats_result_button")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}


          {/* Logos row – only for visitors / non-paid */}
          {!isPaidUser && (
            <>
              <div className="mt-8">
                <p className="mt-8 text-center text-[12px] text-slate-500 tracking-wide">
                  {tUI(language, "trusted_by")}
                </p>
              </div>
              <BrandRow />
            </>
          )}
          </section>


        {/* Service circles – ONLY for signed-in users with active plan */}
          {hasActivePlan && user && (
            <section className="mx-auto max-w-[1100px] px-4 pt-20 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
                {/* Smart CV Conversion */}
                {(() => {
                  const enabled = canUseAts;
                  return (
                    <div
                      className={
                        "flex flex-col items-center " +
                        (enabled
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-40")
                      }
                      onClick={() => {
                        if (!enabled) {
                          onOpenSection?.("pricing");
                          return;
                        }
                        onSelectService?.("ats");
                      }}
                    >
                      <div
                        className={
                          "h-40 w-40 rounded-full bg-[#061059] flex items-center justify-center text-white text-[16px] font-semibold px-4 shadow-[0_0_35px_rgba(56,89,248,0.30)] border-4 " +
                          (service === "ats"
                            ? "border-[#cbd5f5]"
                            : "border-transparent hover:border-[#cbd5f5]")
                        }
                      >
                        Smart CV Conversion
                      </div>
                      <p className="mt-4 text-[13px] text-slate-600 leading-5 max-w-[260px]">
                        {tUI(language, "services_ats_desc")}
                      </p>
                    </div>
                  );
                })()}

                {/* AI-Enhanced CV Matching */}
                {(() => {
                  const enabled = canUseAi;
                  return (
                    <div
                      className={
                        "flex flex-col items-center " +
                        (enabled
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-40")
                      }
                      onClick={() => {
                        if (!enabled) {
                          onOpenSection?.("pricing");
                          return;
                        }
                        onSelectService?.("ai_matching");
                      }}
                    >
                      <div
                        className={
                          "h-40 w-40 rounded-full bg-[#061059] flex items-center justify-center text-white text-[16px] font-semibold px-4 shadow-[0_0_35px_rgba(56,89,248,0.30)] border-4 " +
                          (service === "ai_matching"
                            ? "border-[#cbd5f5]"
                            : "border-transparent hover:border-[#cbd5f5]")
                        }
                      >
                        AI-Enhanced CV Matching
                      </div>
                      <p className="mt-4 text-[13px] text-slate-600 leading-5 max-w-[260px]">
                        {tUI(language, "services_ai_desc")}
                      </p>
                    </div>
                  );
                })()}

                {/* Premium CV Builder */}
                {(() => {
                  const enabled = canUseBuilder;
                  return (
                    <div
                      className={
                        "flex flex-col items-center " +
                        (enabled
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-40")
                      }
                      onClick={() => {
                        if (!enabled) {
                          onOpenSection?.("pricing");
                          return;
                        }
                        onSelectService?.("builder");
                      }}
                    >
                      <div
                        className={
                          "h-40 w-40 rounded-full bg-[#061059] flex items-center justify-center text-white text-[16px] font-semibold px-4 shadow-[0_0_35px_rgba(56,89,248,0.30)] border-4 " +
                          (service === "builder"
                            ? "border-[#cbd5f5]"
                            : "border-transparent hover:border-[#cbd5f5]")
                        }
                      >
                        Premium CV Builder
                      </div>
                      <p className="mt-4 text-[13px] text-slate-600 leading-5 max-w-[260px]">
                        {tUI(language, "services_builder_desc")}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </section>
          )}



          {/* Pricing – only for users WITHOUT an active plan */}
          

          {/* Badges – marketing only for non-paid */}
          {!isPaidUser && (
            <div className="mx-auto max-w-[1140px] px-4">
              <div className="mb-8 flex items-center justify-center gap-12 text-[14px] text-slate-700">
                <Badge icon={Lock} label={tUI(language, "badge_gdpr")} />
                <Badge
                  icon={Shield}
                  label={tUI(language, "badge_secure")}
                />
              </div>
            </div>
          )}

          {/* Video section (landing) */}
          {/* Video demo (modern) */}
        {!hasActivePlan && (
          <div className="mx-auto mt-12 mb-20 w-full max-w-[1100px] px-4">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              {/* subtle gradient top wash */}
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-24 left-1/2 h-56 w-[720px] -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-100 via-blue-50 to-sky-100 blur-3xl opacity-70" />
              </div>

              <div className="relative p-6 md:p-8">
                {/* header */}
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="max-w-[720px]">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-[12px] font-semibold text-slate-700 backdrop-blur">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                      {tUI(language, "video_badge")}
                    </div>

                    <h3 className="text-left text-[22px] md:text-[28px] font-extrabold tracking-tight text-slate-900">
                      {tUI(language, "video_title")}
                    </h3>

                    <p className="mt-2 text-left text-[14px] md:text-[15px] leading-6 text-slate-600">
                      {tUI(language, "video_desc")}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* <button
                      type="button"
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] font-semibold text-slate-900 hover:bg-slate-50"
                      onClick={() => onOpenSection?.("pricing")}
                    >
                      See pricing
                    </button> */}

                    <button
                      type="button"
                      className="rounded-full bg-[#061059] px-5 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-[#0A1575]"
                      onClick={() => {
                      trackEvent("unlock_full_cv_clicked", {
                        location: "video_section",
                        plan: "premium",
                      });

                      onLandingUnlock?.();
                    }}
                    >
                      {tUI(language, "preview_unlock")}
                    </button>
                  </div>
                </div>

                {/* video frame */}
                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-black shadow-[0_18px_50px_rgba(2,6,23,0.25)]">
                  <div className="relative aspect-video">
                    {/* optional: subtle “glass” overlay ring */}
                    <div className="pointer-events-none absolute inset-0 ring-1 ring-white/10" />

                    <video
                    controls
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover pointer-events-auto"
                  >
                    <source src={`${process.env.PUBLIC_URL}/video/demo.mp4`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  </div>
                </div>

                {/* small footnote */}
                <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <p className="text-left text-[12px] text-slate-500">
                    {tUI(language, "hint_secure")}
                  </p>
                  <p className="text-left text-[12px] text-slate-500">
                    {tUI(language, "preview_sub")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}
  
          {/* Why Choose Us – only for non-paid */}
          {!isPaidUser && (
            <section className="mx-auto max-w-[1140px] px-4 pb-16">
              <h2 className="text-center text-[28px] md:text-[32px] font-bold tracking-tight">
                {tUI(language, "why_title")}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
                {[
                  {
                    icon: Target,
                    title: tUI(language, "why_ats_title"),
                    desc: tUI(language, "why_ats_desc"),
                  },
                  {
                    icon: Spark,
                    title: tUI(language, "why_ai_title"),
                    desc: tUI(language, "why_ai_desc"),
                  },
                  {
                    icon: Shield,
                    title: tUI(language, "why_easy_title"),
                    desc: tUI(language, "why_easy_desc"),
                  },
                  {
                    icon: Device,
                    title: tUI(language, "why_formats_title"),
                    desc: tUI(language, "why_formats_desc"),
                  },
                ].map((f, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl border border-[#06105933] p-6 text-center shadow-sm"
                  >
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-[#06105933]">
                      <f.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-[16px] font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-[13px] leading-5 text-slate-600">
                      {f.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ATS Explainer + Testimonials – only for non-paid */}
          {!isPaidUser && (
            <section className="mx-auto max-w-[1140px] px-4 pb-24">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* LEFT: ATS + AI Explainer */}
                <div>
                  <h3 className="text-[26px] font-bold tracking-tight">
                    {tUI(language, "ats_title")}
                  </h3>

                  <p className="mt-2 text-[14px] text-slate-600 max-w-xl">
                    {tUI(language, "ats_sub")}
                  </p>

                  <div className="mt-6 space-y-6">
                    {/* Item 1 */}
                    <div className="flex items-start gap-4">
                      <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#06105933] text-slate-700">
                        <svg
                          className="h-6 w-6"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="5" width="18" height="12" rx="2" />
                          <path d="M7 21h10" />
                        </svg>
                      </span>
                      <div>
                        <div className="text-[15px] font-semibold text-slate-900">
                          {tUI(language, "ats_item1_title")}
                        </div>
                        <p className="text-[13px] text-slate-600 leading-5">
                          {tUI(language, "ats_item1_desc")}
                        </p>
                      </div>
                    </div>

                    {/* Item 2 */}
                    <div className="flex items-start gap-4">
                      <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#06105933] text-slate-700">
                        <svg
                          className="h-6 w-6"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M10.29 3.86l-8.48 14.72A2 2 0 0 0 3.52 22h16.96a2 2 0 0 0 1.71-3.42L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <path d="M12 9v4m0 4h.01" />
                        </svg>
                      </span>
                      <div>
                        <div className="text-[15px] font-semibold text-slate-900">
                          {tUI(language, "ats_item2_title")}
                        </div>
                        <p className="text-[13px] text-slate-600 leading-5">
                          {tUI(language, "ats_item2_desc")}
                        </p>
                      </div>
                    </div>
                    {/* Item 3 */}
                    <div className="flex items-start gap-4">
                      <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#06105933] text-slate-700">
                        <span className="text-[16px] font-semibold leading-none">AI</span>
                      </span>
                      <div>
                        <div className="text-[15px] font-semibold text-slate-900">
                          {tUI(language, "ats_item3_title")}
                        </div>
                        <p className="text-[13px] text-slate-600 leading-5">
                          {tUI(language, "ats_item3_desc")}
                        </p>
                      </div>
                    </div>
                    {/* Item 4 */}
                   <div className="flex items-start gap-4">
                      <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#06105933] text-slate-700">
                        <span className="text-[20px] leading-none">✓</span>
                      </span>
                      <div>
                        <div className="text-[15px] font-semibold text-slate-900">
                          {tUI(language, "ats_item4_title")}
                        </div>
                        <p className="text-[13px] text-slate-600 leading-5">
                          {tUI(language, "ats_item4_desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Testimonials */}
                <div className="space-y-4">
                  <p className="text-[13px] uppercase tracking-wide text-slate-500 mb-5">
                    {tUI(language, "ats_testimonial_title")}
                  </p>

                  <div className="space-y-4">
                    {/* Testimonial 1 */}
                    <div className="rounded-2xl border border-[#06105933] bg-white p-6 shadow-sm">
                      <span
                        className="text-slate-300 text-6xl leading-none select-none"
                        style={{
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          lineHeight: "0.5",
                          marginBottom: "-20px",
                        }}
                      >
                        &ldquo;
                      </span>
                   <p className="text-[16px] leading-6 text-slate-700">
                        “{tUI(language, "testimonial_1_text")}”
                      </p>
                      <p className="mt-3 text-[12px] text-slate-500">
                        {tUI(language, "testimonial_1_author")}
                      </p>
                    </div>

                    {/* Testimonial 2 */}
                    <div className="rounded-2xl border border-[#06105933] bg-white p-6 shadow-sm">
                      <span
                        className="text-slate-300 text-6xl leading-none select-none"
                        style={{
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          lineHeight: "0.5",
                          marginBottom: "-20px",
                        }}
                      >
                        &ldquo;
                      </span>
                    <p className="text-[16px] leading-6 text-slate-700">
                        “{tUI(language, "testimonial_2_text")}”
                      </p>
                      <p className="mt-3 text-[12px] text-slate-500">
                        {tUI(language, "testimonial_2_author")}
                      </p>
                    </div>

                    {/* Testimonial 3 */}
                    <div className="rounded-2xl border border-[#06105933] bg-white p-6 shadow-sm">
                      <span
                        className="text-slate-300 text-6xl leading-none select-none"
                        style={{
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          lineHeight: "0.5",
                          marginBottom: "-20px",
                        }}
                      >
                        &ldquo;
                      </span>
                      <p className="text-[16px] leading-6 text-slate-700">
                        “{tUI(language, "testimonial_3_text")}”
                      </p>
                      <p className="mt-3 text-[12px] text-slate-500">
                        {tUI(language, "testimonial_3_author")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
